import React, {  useEffect, useState } from "react";
import styled from "@emotion/styled";
import SkeletonLayout from "./skeleton/SkeletonLayout";
import {
  Table,
  TableContainer,
  Paper,
  Pagination,
  Grid,
  TableBody,
  Stack,
} from "@mui/material";
import { useGetCampaignListQuery, useGetEmailVerifiedQuery, useLazyTrackHealthQuery } from "../../../redux/services/folderName";
import TableHeader from "./tableHeader/TableHeader";
import TableContent from "./tableBody/TableContent";
import { Filters } from "../filters/Filters";
import { COLOR_MAP } from "../../../constants/ColorNames";
import { useSearchParams } from "react-router-dom";
import { CampaignListDataType, ConfigureMailDataType, FiltersType } from "../../../types/dataTypes";
import { getHealthApiPayload } from "../../../utils/helpers";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import ConfigureMailStrip from "../header/configure-email/ConfigureMailStrip";
import useRevamp from "../../../hooks/useRevamp";
import Banner from "../../../common/components/Banner";
import { setMailStripDataRedux } from "../../../redux/slice/campaignSlice";
import { bannerDataSelector, inventoryUsageSelector } from "../../../redux/selectors/campaignSetup.selector";
import { ToastProvider } from "@foundit/ui-library";

type LayoutProps = {
  isLoading: boolean;
  bannerRef: any;
}

const initialFilters = {
  campaignSearchName: '',
  campaignStatus: '',
  endDate: '',
  startDate: ''
}

const TableWrapper = styled(Grid)`
  padding: 2rem 4rem 4rem 4rem;
  display: flex;
  justify-content: flex-end;  
  gap: 2.4rem;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;

`

const Layout = (props: LayoutProps) => {
  const [pageNo, setPageNo] = React.useState<number>(0);
  
  const [mailStripData, setMailStripData] = React.useState<ConfigureMailDataType>({
    redirectionalLink: '',
    isGmailUser: false
  })

  const { isLoading, bannerRef } = props;
  const userData = useSelector((state: RootState) => state.campaign.userData);
  const {bannerData} = useSelector(bannerDataSelector)
  const {inventoryUsage} = useSelector(inventoryUsageSelector)
  const [trigger] = useLazyTrackHealthQuery();
  const { data: emailVerifiedData, refetch: triggerEmailRefetch, status: emailVerifiedDataStatus} = useGetEmailVerifiedQuery(1);

  const { isRevamp } = useRevamp();

  const startTime = React.useRef(new Date());
  const [ searchParams ] = useSearchParams();
  const queryObject = Object.fromEntries(searchParams.entries());
  const hasTriggered = React.useRef(false);

  const campaignId = searchParams.get('campaign_id'); // string | null
  const campaignName = searchParams.get('campaign_name'); // string | null
  const [filters, setFilters] = React.useState<FiltersType>(() => ({
    ...initialFilters,
    ...(campaignId ? { campaignId } : {}),
    ...(campaignName ? { campaignSearchName: campaignName } : {})
  }));

  const handleFitersValueChange = React.useCallback((filters: FiltersType) => {
    setFilters(filters);
  }, [])

  const { data, isLoading: isTableLoading, isFetching, error } = useGetCampaignListQuery({ pageNo, filters });
  const pageCount = (data && data.totalCount) ? Math.ceil(data.totalCount / 6) : 0;
  const selectedCampaignId = searchParams.get('campaignId');
  const [expanded, setExpanded] = useState<any>([]);



  React.useEffect(() => {
    if((data || error ) && !hasTriggered.current){
      let status = data ? 200 : 400
      const healthPayload: any = 
      getHealthApiPayload(status, queryObject, startTime.current, userData?.recruiterId, 'CTP');
      hasTriggered.current = true; 
      trigger(healthPayload)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[data,error])

  React.useEffect(() => {
    if(!emailVerifiedData) return;

    if(emailVerifiedData && emailVerifiedData?.redirectURL){
      const updatedMailStripData = {
        redirectionalLink: emailVerifiedData.redirectURL,
        isGmailUser: emailVerifiedData.isGmail
      }
      setMailStripData(updatedMailStripData)
      setMailStripDataRedux(updatedMailStripData)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[emailVerifiedData])
  

  const isPassiveJobsCountZero = data?.campaignDetails?.every((campaignDetail: CampaignListDataType) => campaignDetail.passiveJob === 0);
  
  return (
    <>
    <ToastProvider>
      <ConfigureMailStrip mailStripData={mailStripData} emailVerifiedData={emailVerifiedData} status={emailVerifiedDataStatus}/>


      <Filters pageNo={pageNo} handleFitersValueChange={handleFitersValueChange} filteredCampaignName={campaignName||""} />
      <Stack sx={{
          backgroundColor: COLOR_MAP.GREY,
          minHeight: '100vh'
      }}>

       {!!bannerData?.[0]?.message && inventoryUsage?.isLimitReached && (
          <Banner cta={bannerData?.[0]?.cta} bannerRef={bannerRef}>
            <span ref={bannerRef}>{bannerData?.[0]?.message}</span>
          </Banner>
        )}


      <TableWrapper container spacing={1}>
        <TableContainer sx={{ borderRadius: '0.8rem' }} component={Paper}>
          {(isLoading || isTableLoading || isFetching) ? (
            <SkeletonLayout />
          ) : (
            <Table aria-label="simple table">
              <TableHeader showInboundColumn={!isPassiveJobsCountZero}/>
              <TableBody>
                {(data?.campaignDetails || []).map((campaignDetail: CampaignListDataType, index: any) => {
                  const isLast = index === data?.campaignDetails.length - 1;
                  return (
                    <TableContent
                      isLast={isLast}
                      campaignDetail={campaignDetail}
                      selectedCampaignId={selectedCampaignId}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      revampServices={{enabled: isRevamp}}
                      bannerRef={bannerRef}
                      mailStripData={mailStripData}
                    />
                  )
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        {pageCount ? <Pagination onChange={(_, page) => setPageNo(page - 1)} count={pageCount} shape="rounded" /> : null}
      </TableWrapper>
      </Stack>
    </ToastProvider>
    </>
  );
};

export default Layout;
